const pad2 = (n) => String(n).padStart(2, '0');

export const formatLocalDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

export const formatUtcDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
};

export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  return new Date(year, month, day);
};

/**
 * Return the UTC midnight Date for the *local* calendar day represented by `date`.
 * The date picker produces local midnight Dates, so the local components are the
 * day the user actually picked. This preserves that day regardless of the
 * browser's local timezone, avoiding shifts like a "Today" preset sending the
 * previous day's UTC date.
 */
export const toUtcMidnight = (date) => {
  if (!date) return null;
  const d = new Date(date);
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0));
};

/**
 * Return the last UTC instant of the *local* calendar day represented by `date`.
 * The date picker selects whole days, but the API takes an inclusive instant for
 * the end of the range — so a single-day range ("Today") has to close at
 * 23:59:59.999Z rather than at midnight, which would match only rows stamped
 * exactly midnight. Pair with `toUtcMidnight` for the start of the range.
 */
export const toUtcEndOfDay = (date) => {
  if (!date) return null;
  const d = new Date(date);
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999));
};

/**
 * UTC midnight of the calendar day the user *picked*, read from the Date's local components.
 * Date pickers hand back local-midnight Dates, so in a timezone ahead of UTC (e.g. Paris) the UTC
 * components of "today 00:00 local" still say yesterday; reading local components keeps the
 * day the user clicked. Prefer this over toUtcMidnight for picker-derived dates.
 */
export const pickedDayToUtcMidnight = (date) => {
  if (!date) return null;
  const d = new Date(date);
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0));
};

/**
 * Picker range ({ startDate, endDate } calendar days) -> API instants: `from` is UTC midnight of the
 * first picked day and `to` is the last millisecond of the last picked day, so the end day is
 * fully included (and a single-day "Today" range is a whole day, not a zero-length window).
 */
export const pickedRangeToUtc = ({ startDate, endDate }) => {
  const DAY_MS = 24 * 60 * 60 * 1000;
  return {
    from: pickedDayToUtcMidnight(startDate).toISOString(),
    to: new Date(pickedDayToUtcMidnight(endDate).getTime() + DAY_MS - 1).toISOString(),
  };
};
